import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import tinycolor2 from "tinycolor2";

type THasLogos = {
  niceLogo?: IAmericanCompanyIssuerTopLevelFragment["niceLogo"] | IBrazilianCompanyIssuerTopLevelFragment["niceLogo"];
  standardLogo?:
    | IAmericanCompanyIssuerTopLevelFragment["standardLogo"]
    | IBrazilianCompanyIssuerTopLevelFragment["standardLogo"];
};

export default function getHairlineColor(issuer: THasLogos, prefersDark = false) {
  if (issuer.niceLogo) {
    return dominantVsPaletteColor(issuer.niceLogo.dominantColor, issuer.niceLogo.colorPalette || [], prefersDark);
  }
  if (issuer.standardLogo) {
    return dominantVsPaletteColor(
      issuer.standardLogo.dominantColor,
      issuer.standardLogo.colorPalette || [],
      prefersDark,
    );
  }
  return null;
}

function dominantVsPaletteColor(dominantColor: string | null, colorPalette: string[], prefersDark: boolean) {
  if (prefersDark) {
    const dominanceScores = colorPalette.map((color) => calculateDominanceScore(tinycolor2(color)));
    const maxScoreIndex = dominanceScores.indexOf(Math.max(...dominanceScores));
    const mostDominantColor = colorPalette[maxScoreIndex];
    if (mostDominantColor) {
      return mostDominantColor;
    }
  }
  const isDarkOrVoid = dominantColor ? tinycolor2(dominantColor).isDark() : true;
  return isDarkOrVoid
    ? [...colorPalette].sort((a, b) => {
        return prefersDark
          ? tinycolor2(a).getBrightness() - tinycolor2(b).getBrightness()
          : tinycolor2(b).getBrightness() - tinycolor2(a).getBrightness();
      })[0]
    : dominantColor;
}

function calculateSaturation(r: number, g: number, b: number) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === 0) {
    return 0;
  }
  return (max - min) / max;
}

function calculateDominanceScore(color: tinycolor2.Instance) {
  const { r, g, b } = color.toRgb();

  // Define weights for different color attributes
  const luminance = 0.2;
  const saturation = 0.5;
  const hue = 0.2;

  // Calculate dominance score using weighted factors
  return luminance * color.getLuminance() + saturation * calculateSaturation(r, g, b) + hue * color.getAlpha();
}
